import { FC, Suspense, useCallback, useContext } from 'react';
import { Box, Button, Grid, Paper, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { DialogContext, LocalizationContext } from '@cvt/contexts';
import { BodyLoading } from '@cvt/components/layout/BodyLoading';
import { EditNoteRounded, Restore } from '@mui/icons-material';
import { ArchetypeShareButton } from '@shared/components/ArchetypeShareButton';

import { useLeaveReview } from '@shared/hooks/useLeaveReview';

import { AuthContext } from '@modules/Auth/contexts';
import { useMe } from '@modules/Auth/hooks/useMe';

import { AcademicArchetype } from '../components/AcademicArchetype';
import { useUserCrud } from '../hooks/useUserCrud';
import { UserForm } from '../partials/UserForm';

export const Profile: FC = () => {

  const { dictionary } = useContext(LocalizationContext);
  const { asyncConfirmation } = useContext(DialogContext);
  const { logout } = useContext(AuthContext);

  const { user, status } = useMe();

  const { editUser, resetAccount } = useUserCrud();

  const onReset = useCallback(async () => {
    const userConfirmed = await asyncConfirmation({
      title: 'Reset Account',
      content: 'Are you sure you want to reset your account? This action cannot be undone.',
      confirmLabel: 'Reset',
      cancelLabel: 'Cancel',
    });

    if (!userConfirmed) {
      return false;
    }
    return resetAccount.mutateAsync();
  }, [asyncConfirmation, resetAccount]);

  const onSubmit = useCallback((data: Users.Crud) => {
    if (!user) {
      return false;
    }
    return editUser({
      ...data,
      id: user.id,
    });
  }, [user, editUser]);

  const onLeaveReview = useLeaveReview();

  return (
    <>
      {status !== 'success' || !user ? (
        <BodyLoading height="100dvh"/>
      ) : (
        <Grid container spacing={2}>
          <Grid size={12}>
            <Typography variant="h1" fontSize={24} fontWeight={600}>{user.displayName}</Typography>
          </Grid>
          <Grid size={12}>
            <Box component={Paper} variant="elevation" elevation={2} p={2}>
              <UserForm
                defaultValues={{
                  ...user,
                  location: user.location ? {
                    id: user.location.id,
                    name: user.location.title,
                    location: {
                      lat: user.location.latitude,
                      lng: user.location.longitude,
                    },
                  } : null,
                }}
                disabledFields={['type']}
                onSubmitRequest={onSubmit}
                onSubmitButtonText={dictionary.forms.buttonEdit}
                slots={{
                  header: (
                    <AcademicArchetype
                      archetype={user.archetype}
                      archetypeDescription={user.archetypeDescription}
                      showFindOut
                      slots={{
                        share: user.archetype ? (
                          <Suspense fallback={null}>
                            <ArchetypeShareButton archetype={user.archetype} archetypeDescription={user.archetypeDescription} />
                          </Suspense>
                        ) : undefined,
                      }}
                      sx={{ boxShadow: 'none', border: 'none' }}
                    />
                  ),
                }}
              />
            </Box>
          </Grid>
          <LoadingButton
            fullWidth
            variant="contained"
            color="inherit"
            size="large"
            onClick={onLeaveReview}
            startIcon={<EditNoteRounded/>}
          >
            Leave Review
          </LoadingButton>
          <LoadingButton
            fullWidth
            loading={resetAccount.isPending}
            variant="contained"
            color="warning"
            size="large"
            onClick={onReset}
            startIcon={<Restore/>}
          >

            Reset Account
          </LoadingButton>
          <Button fullWidth color="inherit" variant="text" size="large" onClick={logout}>Logout</Button>
        </Grid>
      )}
    </>
  );
};
